import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { CalendarDays } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import type { RdScheduleBlock } from "../../../shared/rd-schedule";
import type {
  AlwaysAiUser,
  AppointmentDetailResponse,
  AppointmentNoteResponse,
} from "../../../shared/api-types";
import {
  AppointmentAgenda,
  GreetingHeading,
  type AppointmentAgendaHeader,
  type AppointmentView,
  type AppointmentWeekSlot,
} from "@/components/appointments/appointment-agenda";
import { AppointmentWeekPanel } from "@/components/appointments/appointment-week-panel";
import {
  useAppointmentActionDialogs,
  type AppointmentActionDialogsController,
} from "@/components/rd-schedule/appointment-action-dialogs";
import { useBlockTimeAction } from "@/components/v2/block-time-dialog";
import { RemoveBlockTimeDialog } from "@/components/v2/remove-block-time-dialog";
import { PageAppointmentDialogs } from "@/components/rd-schedule/page-appointment-dialogs";
import { WeekAppointmentActions } from "@/components/rd-schedule/week-appointment-actions";
import type { WeekViewData } from "@/components/v2/week-view";
import { useShellFrame } from "@/components/v2/shell-layout";
import { useRdScheduleWeek } from "@/lib/use-rd-schedule-week";
import { weekViewEnabled } from "@/lib/rd-schedule-week";
import {
  AppointmentPastWeek,
  type PastWeekPanelState,
  type PastWeekRowLinkProps,
} from "@/components/appointments/appointment-past-week";
import { AppointmentDetail } from "@/components/appointments/appointment-detail";
import { AgendaStatusSplitProvider } from "@/components/appointments/appointment-status";
import { AppointmentScopeSelector } from "@/components/appointments/appointment-scope-selector";
import {
  AGENDA_SKELETON_CARD,
  PAGE_TITLE,
} from "@/components/appointments/agenda-look";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import { NotFound } from "@/components/not-found";
import { RdHoursEditor } from "@/components/v2/rd-hours/editor";
import { authenticatedApi } from "@/lib/api";
import { availabilityGate, availabilityHeading } from "@/lib/rd-hours-client";
import { defaultWeekStart, earlierWeek, laterWeek } from "@/lib/appointment-past-week";
import {
  appointmentListHref,
  appointmentStatusSearch,
  appointmentViewFor,
  appointmentViewSearch,
  appointmentWeekSearch,
  appointmentWeekViewSearch,
  deviceTimezone,
  nextAppointmentBatchSearch,
  type AppointmentListSearch,
} from "@/lib/appointments";
import {
  appointmentLiveText,
  pastWeekLiveText,
  pastWeekQueryKey,
  type PastLiveState,
} from "@/lib/appointment-past-week";
import {
  appointmentRestoreRecords,
  isPlainPrimaryActivation,
} from "@/lib/appointment-restore-record";
import {
  appointmentScopeFor,
  appointmentUnavailableCopy,
} from "@/lib/appointment-scope-state";
import { useAppointmentAgenda } from "@/lib/use-appointment-agenda";
import {
  useAppointmentPastWeek,
  type AppointmentPastWeek as AppointmentPastWeekState,
} from "@/lib/use-appointment-past-week";
import {
  can,
  canManageRdHours,
  canSelectAppointmentRd,
  readsOwnAppointmentRows,
} from "@/lib/permissions";
import { EmptyState, Skeleton, cn } from "@ndd/ui";
import { useViewedRd } from "@/lib/use-viewed-rd-name";
import { useFeatures } from "@/lib/use-features";
import { RD_SCHEDULE_FLAG } from "../../../shared/features";

function useCurrentUser() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ["current-user"],
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });
}

// A viewer who can name an RD gets the title as the picker (NO-467), the
// same shape as the /rds roster title: it names whose schedule is on screen,
// stays mounted across every state, and is visible at every width. An RD who
// cannot select keeps their greeting.
function renderScopeSelector(
  user: AlwaysAiUser | undefined,
  search: AppointmentListSearch,
  viewedRdName: string | null,
  headingRef: RefObject<HTMLHeadingElement | null>,
  view: AppointmentView
): ReactElement | null {
  if (!user || !canSelectAppointmentRd(user)) return null;

  const holdsOwnRows = can(user, "appointments:read:own");

  return (
    <AppointmentScopeSelector
      scope={appointmentScopeFor(search.rdId, holdsOwnRows)}
      rdName={viewedRdName}
      holdsOwnRows={holdsOwnRows}
      headingRef={headingRef}
      view={view}
    />
  );
}

function usePastWeekFor(
  search: AppointmentListSearch,
  view: AppointmentView,
  navigate: Navigate
) {
  return useAppointmentPastWeek(search, view === "past", (week) => {
    void navigate({
      to: "/appointments",
      search: appointmentWeekSearch(search, week),
      replace: true,
    });
  });
}

function availabilityPanel(
  gate: ReturnType<typeof availabilityGate>,
  user: AlwaysAiUser | undefined,
  search: AppointmentListSearch,
  navigate: Navigate
): ReactElement | undefined {
  if (gate !== "open" || !user) return undefined;

  return (
    <AvailabilityPanel
      user={user}
      search={search}
      onSearchChange={(next) => {
        void navigate({ to: "/appointments", search: next });
      }}
    />
  );
}

/** Blank while flags and the viewer load, the 404 when the gate is closed. */
function availabilityGateScreen(
  view: AppointmentView,
  gate: ReturnType<typeof availabilityGate>
): ReactElement | null | undefined {
  if (view !== "availability" || gate === "open") return undefined;

  return gate === "blank" ? null : <NotFound />;
}

export function AppointmentsPage(): ReactElement | null {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as AppointmentListSearch;
  const me = useCurrentUser();
  const user = me.data?.user;
  const { features, weekRoute, view, awaitingWeek, gate } = useViewGate(
    search,
    user,
    me.isPending
  );
  // Availability reads only /api/rd-hours: no Appointment list, no Past week,
  // so a supervisor's agenda refusal never blocks it.
  const agenda = useAppointmentAgenda(search, view !== "availability");
  const past = usePastWeekFor(search, view, navigate);
  const viewingOther = user ? !readsOwnAppointmentRows(user, search.rdId) : false;
  const viewedRd = useViewedRd(viewingOther ? search.rdId : undefined);
  const viewedRdName = viewedRd?.name ?? null;
  const headingRef = useHeadingFocusAfterFinalBatch(
    agenda.status === "ready" && agenda.hasMore
  );
  const restore = usePastWeekRestore(past, search);
  const scopeSelector = renderScopeSelector(
    user,
    search,
    viewedRdName,
    headingRef,
    view
  );
  const handlers = appointmentListHandlers(navigate, search, agenda);
  const availability = availabilityPanel(gate, user, search, navigate);

  // Outside the branching: loading, error, no-selection, and unavailable are all
  // still the Appointments route and carry the same trail. The selected RD is NOT
  // a segment — the title is that, and a crumb naming the RD would assert a
  // schedule these states never fetched.
  const gateScreen = availabilityGateScreen(view, gate);

  if (gateScreen !== undefined) return gateScreen;

  return (
    <>
      <BreadcrumbSetter segments={[{ label: "Appointments" }]} />
      <AgendaStatusSplitProvider features={features}>
        <AppointmentsContent
          view={view}
          availability={availability}
          awaitingWeek={awaitingWeek}
          weekRoute={weekRoute}
          agenda={agenda}
          past={past}
          restore={restore}
          scopeSelector={scopeSelector}
          viewingOther={viewingOther}
          viewedRdName={viewedRdName}
          rdMissing={viewedRd?.status === "missing"}
          userName={user?.name}
          headingRef={headingRef}
          listSearch={search}
          showNonDefaultStatuses={search.showNonDefaultStatuses === true}
          {...handlers}
        />
      </AgendaStatusSplitProvider>
    </>
  );
}

type Navigate = ReturnType<typeof useNavigate>;

function appointmentListHandlers(
  navigate: Navigate,
  search: AppointmentListSearch,
  agenda: AppointmentAgendaState
) {
  return {
    onStatusChange: (checked: boolean): void => {
      void navigate({
        to: "/appointments",
        search: appointmentStatusSearch(search, checked),
      });
    },
    onLoadMore: (): void => {
      if (agenda.status !== "ready") return;

      agenda.loadMore();
      void navigate({
        to: "/appointments",
        search: nextAppointmentBatchSearch(search, agenda.loadedBatches),
        replace: true,
      });
    },
    // The switch pushes, so Back returns to the other view; a week change
    // replaces, so Back leaves the page rather than stepping through weeks.
    onViewChange: (next: AppointmentView): void => {
      void navigate({
        to: "/appointments",
        search: appointmentViewSearch(search, next),
      });
    },
    onWeekChange: (week: string): void => {
      void navigate({
        to: "/appointments",
        search: appointmentWeekSearch(search, week),
        replace: true,
      });
    },
    onRowActivate: (
      appointmentId: string,
      event: MouseEvent<HTMLAnchorElement>,
      week: string
    ): void => {
      if (!isPlainPrimaryActivation(event)) return;

      appointmentRestoreRecords.write({ week, appointmentId });
    },
  };
}

type AppointmentAgendaState = ReturnType<typeof useAppointmentAgenda>;
type AppointmentListHandlers = ReturnType<typeof appointmentListHandlers>;

interface PastWeekRestore {
  sectionRef: RefObject<HTMLElement | null>;
  headingRef: RefObject<HTMLHeadingElement | null>;
  /** Restoring a week is silent in the live region (approved notes). */
  silent: boolean;
}

/**
 * F1: on the first ready render of a Past week after the page mounts, a waiting
 * record for that week brings its row into view and focuses it when the row is
 * among the week's rows, or focuses the week heading when it is not. A record
 * for any other week is cleared so it cannot move focus later. The record never
 * holds the RD selection (ADR-0052); instead it is cleared whenever the
 * selection changes in-page, so it never crosses selections.
 */
function usePastWeekRestore(
  past: AppointmentPastWeekState,
  search: AppointmentListSearch
): PastWeekRestore {
  const sectionRef = useRef<HTMLElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const settled = useRef(false);
  const rdId = search.rdId ?? null;
  const key = JSON.stringify(
    pastWeekQueryKey({
      week: search.week ?? null,
      showNonDefaultStatuses: search.showNonDefaultStatuses === true,
      timezone: deviceTimezone(),
      rdId: search.rdId,
    })
  );
  const [silentKey, setSilentKey] = useState(() =>
    restoresOnMount(search) ? key : null
  );
  const renderedRdId = useRef(rdId);
  const readyWeek = past.status === "week" && past.phase === "ready" ? past.week : null;

  // Silence covers only the restored week; once the RD moves on, Past speaks.
  useEffect(() => {
    if (silentKey !== null && silentKey !== key) setSilentKey(null);
  }, [key, silentKey]);

  useEffect(() => {
    if (renderedRdId.current === rdId) return;

    renderedRdId.current = rdId;
    appointmentRestoreRecords.clear();
  }, [rdId]);

  useEffect(() => {
    if (settled.current || readyWeek === null || past.status !== "week") return;

    settled.current = true;
    const record = appointmentRestoreRecords.consume({ week: readyWeek });

    if (!record) {
      appointmentRestoreRecords.clear();

      return;
    }

    const isInWeek = past.appointments.some(({ id }) => id === record.appointmentId);
    focusRestoredRow(
      sectionRef.current,
      headingRef.current,
      isInWeek ? record.appointmentId : null
    );
  }, [readyWeek]);

  return { sectionRef, headingRef, silent: silentKey === key };
}

/** A record already waits for the week this page opens on. */
function restoresOnMount(search: AppointmentListSearch): boolean {
  if (search.view !== "past" || !search.week) return false;

  return appointmentRestoreRecords.peek({ week: search.week });
}

function focusRestoredRow(
  section: HTMLElement | null,
  heading: HTMLHeadingElement | null,
  appointmentId: string | null
): void {
  const rows = section?.querySelectorAll<HTMLElement>("[data-appointment-id]") ?? [];
  const row = [...rows].find(
    (element) => element.dataset.appointmentId === appointmentId
  );

  if (!row) {
    heading?.focus();

    return;
  }

  // Scrolls the shell's <main> scroll container, not the window.
  row.scrollIntoView({ block: "center" });
  row.focus({ preventScroll: true });
}

interface AppointmentsContentProps extends Omit<
  AppointmentListHandlers,
  "onRowActivate"
> {
  view: AppointmentView;
  agenda: AppointmentAgendaState;
  past: AppointmentPastWeekState;
  restore: PastWeekRestore;
  scopeSelector: ReactElement | null;
  /** The Availability panel; undefined while the gate is not open. */
  availability: ReactNode;
  viewingOther: boolean;
  viewedRdName: string | null;
  rdMissing: boolean;
  userName?: string;
  headingRef: RefObject<HTMLHeadingElement | null>;
  listSearch: AppointmentListSearch;
  showNonDefaultStatuses: boolean;
  onRowActivate: AppointmentListHandlers["onRowActivate"];
  /** NO-874: the address names Week and the flag's answer has not arrived. */
  awaitingWeek: boolean;
  /** NO-874: the Week view's wiring; null when it cannot show (flag off, classic frame). */
  weekRoute: WeekRoute | null;
}

function AppointmentsContent(props: AppointmentsContentProps): ReactElement {
  if (props.awaitingWeek) return appointmentLoading(props.scopeSelector);

  if (props.view === "availability") return <AvailabilityFrame {...props} />;

  if (props.view === "week") return <WeekAppointmentsContent {...props} />;

  if (props.view === "past") return <PastAppointmentsContent {...props} />;

  const { agenda, scopeSelector, viewingOther, viewedRdName, rdMissing } = props;

  if (agenda.status === "error") {
    return (
      <QueryErrorState
        error={agenda.error}
        notFoundText="Appointments are unavailable."
        onRetry={agenda.retry}
        className="h-full"
      />
    );
  }

  if (agenda.status === "loading") return appointmentLoading(scopeSelector);

  if (agenda.status === "no_selection") {
    return <NoAppointmentSelection heading={scopeSelector} />;
  }

  if (agenda.status === "unavailable") {
    return (
      <UnavailableAppointments
        reason={agenda.reason}
        viewingOther={viewingOther}
        rdName={viewedRdName}
        rdMissing={rdMissing}
        heading={scopeSelector}
      />
    );
  }

  return (
    <AgendaFrame
      {...props}
      header={{
        display: agenda.display,
        todayCount: agenda.todayCount,
        healthieCalendarUrl: agenda.healthieCalendarUrl,
      }}
      liveText={appointmentLiveText({ view: "upcoming", upcoming: agenda, past: "" })}
      pastPanel={null}
      pastBusy={false}
    />
  );
}

/**
 * NO-874: the view the page shows. Week needs rd-schedule in the single-rail
 * frame, and a Week address waits for the flag's answer instead of flashing
 * Upcoming.
 */
function useViewGate(
  search: AppointmentListSearch,
  user: AlwaysAiUser | undefined,
  userPending: boolean
) {
  const featuresQuery = useFeatures();
  const features = featuresQuery.data?.data;
  const frame = useShellFrame();
  const weekEnabled = weekViewEnabled(features, frame);
  const view: AppointmentView = appointmentViewFor(search, weekEnabled);
  // NO-878: Availability has its own gate (blank, 404, or open).
  const gate = availabilityGate({
    frame,
    loading: featuresQuery.isPending || userPending,
    rdScheduleEnabled: features?.[RD_SCHEDULE_FLAG] === true,
    canManageHours: user ? canManageRdHours(user) : false,
  });

  const weekRoute = useWeekRoute(
    search,
    view === "week",
    user?.id ?? null,
    gate === "open"
  );

  return {
    features,
    weekRoute: weekEnabled ? weekRoute : null,
    view,
    gate,
    awaitingWeek: search.view === "week" && featuresQuery.isPending,
  };
}

/** NO-874: everything the Week view needs from the page. */
interface WeekRoute {
  query: ReturnType<typeof useRdScheduleWeek>;
  /** The ISO Monday on screen. */
  weekStart: string;
  userId: string | null;
  onChangeWeek: (week: string | null) => void;
  onOpenDetails: (appointmentId: string) => void;
  /** NO-876: the block whose details panel is open. */
  selectedId: string | null;
  onSelect: (appointmentId: string | null) => void;
  /** NO-876: the appointment dialogs behind the panel's footer and New appointment (hosted at page level, NO-912). */
  dialogs: AppointmentActionDialogsController;
  /** NO-876: the RD a supervisor or admin acts for. */
  rdId: string | null;
  /** NO-876: opens the visit's own page, where its chart note is written. */
  onOpenVisitPage: (appointmentId: string) => void;
  onSetHours: (() => void) | null;
}

function useWeekRoute(
  search: AppointmentListSearch,
  enabled: boolean,
  userId: string | null,
  availabilityOpen: boolean
): WeekRoute {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const dialogs = useAppointmentActionDialogs();
  const timezone = deviceTimezone();
  // Saturday and Sunday open on the coming week (NO-907).
  const defaultWeek = defaultWeekStart(new Date().toISOString(), timezone);
  const query = useRdScheduleWeek(
    {
      week: search.week ?? defaultWeek,
      timezone,
      ...(search.rdId ? { rdId: search.rdId } : {}),
    },
    enabled
  );

  return {
    query,
    weekStart: search.week ?? defaultWeek,
    userId,
    // A week change replaces, so Back leaves the page rather than stepping
    // through weeks. The current week is left unnamed in the address.
    onChangeWeek: (week) =>
      void navigate({
        to: "/appointments",
        search: appointmentWeekViewSearch(search, week === defaultWeek ? null : week),
        replace: true,
      }),
    // A block opens the details panel (NO-875); its footer opens the dialogs.
    onOpenDetails: setSelectedId,
    selectedId,
    onSelect: setSelectedId,
    dialogs,
    rdId: search.rdId ?? null,
    // The visit's own page, carrying the Week address so its breadcrumb comes
    // back to this week.
    onOpenVisitPage: (appointmentId) =>
      void navigate({
        to: "/appointments/$appointmentId",
        params: { appointmentId },
        search: appointmentWeekViewSearch(search, search.week ?? null),
      }),
    // "Set your hours" opens the Availability view (NO-878), keeping the
    // selected RD. It is offered only while that view's gate is open, so it
    // never lands on a 404.
    onSetHours: availabilityOpen
      ? () =>
          void navigate({
            to: "/appointments",
            search: appointmentViewSearch(search, "availability"),
          })
      : null,
  };
}

/**
 * The page's header actions and the dialogs behind them (NO-877, NO-912). A
 * hook because the Block time dialog holds state. Both dialogs mount at page
 * level, so Block time and New appointment work from every tab; they are off
 * only while the `rd-schedule` gate is closed (no route).
 */
function useWeekSlot(props: AppointmentsContentProps): AppointmentWeekSlot | null {
  const route = props.weekRoute;
  const blockTime = useBlockTimeAction(props.listSearch.rdId ?? null, route !== null);

  return weekSlotFor(route, props.view, blockTime);
}

function weekSlotFor(
  route: WeekRoute | null,
  view: AppointmentView,
  blockTime: ReturnType<typeof useBlockTimeAction>
): AppointmentWeekSlot | null {
  if (!route) return null;

  const { query } = route;
  const body = query.data?.data;

  return {
    panel: view === "week" ? <WeekRoutePanel route={route} /> : null,
    busy: view === "week" && query.isFetching,
    onBlockTime: blockTime.onBlockTime,
    onNewAppointment: () => route.dialogs.openNew(),
    dialogs: (
      <>
        {blockTime.dialog}
        <PageAppointmentDialogs
          controller={route.dialogs}
          rdId={route.rdId}
          timeZone={body?.status === "ok" ? body.timezone : deviceTimezone()}
          onBackToDetails={route.onSelect}
        />
      </>
    ),
  };
}

function WeekRoutePanel({ route }: { route: WeekRoute }): ReactElement {
  const { query, weekStart, userId } = route;
  const body = query.data?.data;
  const [removing, setRemoving] = useState<RdScheduleBlock | null>(null);

  const data: WeekViewData =
    body?.status === "ok"
      ? { kind: "ready", week: body, refreshing: query.isPlaceholderData }
      : query.isError
        ? { kind: "error", weekStart, onRetry: () => void query.refetch() }
        : { kind: "loading", weekStart };

  return (
    <>
      <AppointmentWeekPanel
        // The remembered Key is read once per user, so a late-arriving user id remounts.
        key={userId ?? "pending"}
        userId={userId}
        data={data}
        onPreviousWeek={() => route.onChangeWeek(earlierWeek(weekStart))}
        onNextWeek={() => route.onChangeWeek(laterWeek(weekStart))}
        onToday={() => route.onChangeWeek(null)}
        onOpenWeek={(week) => route.onChangeWeek(week)}
        onOpenDetails={route.onOpenDetails}
        selectedId={route.selectedId}
        onOpenBlock={setRemoving}
        onSetHours={route.onSetHours}
      />
      {body?.status === "ok" ? (
        <RemoveBlockTimeDialog
          block={removing}
          timeZone={body.timezone}
          rdId={route.rdId}
          onClose={() => setRemoving(null)}
        />
      ) : null}
      {body?.status === "ok" ? (
        <WeekAppointmentActions
          week={body}
          selectedId={route.selectedId}
          onSelect={route.onSelect}
          controller={route.dialogs}
          onOpenVisitPage={route.onOpenVisitPage}
          rdId={route.rdId}
        />
      ) : null}
    </>
  );
}

/**
 * NO-874: the Week view. The header stays on every state that has a schedule;
 * no selection and an unavailable Provider replace the page as in the other
 * views.
 */
function WeekAppointmentsContent(props: AppointmentsContentProps): ReactElement {
  const { scopeSelector, viewingOther, viewedRdName, rdMissing, weekRoute } = props;
  const body = weekRoute?.query.data?.data;

  if (body?.status === "no_selection") {
    return <NoAppointmentSelection heading={scopeSelector} />;
  }

  if (body?.status === "unavailable") {
    return (
      <UnavailableAppointments
        reason={body.reason}
        viewingOther={viewingOther}
        rdName={viewedRdName}
        rdMissing={rdMissing}
        heading={scopeSelector}
      />
    );
  }

  return (
    <AgendaFrame
      {...props}
      header={weekHeader(props.agenda)}
      liveText=""
      pastPanel={null}
      pastBusy={false}
    />
  );
}

function weekHeader(agenda: AppointmentAgendaState): AppointmentAgendaHeader {
  if (agenda.status === "ready") {
    return {
      display: agenda.display,
      todayCount: agenda.todayCount,
      healthieCalendarUrl: null,
    };
  }

  return { display: deviceClock(), todayCount: null, healthieCalendarUrl: null };
}

/**
 * F4b: in Past the page follows the Past week query. The Upcoming list only
 * lends the date line its count and the Healthie link; when it is not ready
 * the date line comes from the Past answer (or the device clock) without one.
 */
function PastAppointmentsContent(props: AppointmentsContentProps): ReactElement {
  const { past, scopeSelector, viewingOther, viewedRdName, rdMissing } = props;

  if (past.status === "no_selection") {
    return <NoAppointmentSelection heading={scopeSelector} />;
  }

  if (past.status === "unavailable") {
    return (
      <UnavailableAppointments
        reason={past.reason}
        viewingOther={viewingOther}
        rdName={viewedRdName}
        rdMissing={rdMissing}
        heading={scopeSelector}
      />
    );
  }

  if (past.status === "idle") return appointmentLoading(scopeSelector);

  return (
    <AgendaFrame
      {...props}
      header={pastHeader(props.agenda, past)}
      liveText={appointmentLiveText({
        view: "past",
        upcoming: null,
        past: pastWeekLiveText(
          pastLiveState(past, props.restore.silent),
          pastClock(past)
        ),
      })}
      pastPanel={<PastPanel {...props} past={past} />}
      pastBusy={isPastBusy(past)}
    />
  );
}

function PastPanel(
  props: AppointmentsContentProps & { past: PastWeekPanelState }
): ReactElement {
  const { past, onRowActivate } = props;

  return (
    <AppointmentPastWeek
      state={past}
      viewingOther={props.viewingOther}
      listSearch={props.listSearch}
      showNonDefaultStatuses={props.showNonDefaultStatuses}
      onStatusChange={props.onStatusChange}
      onWeekChange={props.onWeekChange}
      onRowActivate={(appointmentId, event) => {
        if (past.status === "week") onRowActivate(appointmentId, event, past.week);
      }}
      renderLink={renderPastRowLink}
      headingRef={props.restore.headingRef}
      sectionRef={props.restore.sectionRef}
    />
  );
}

function renderPastRowLink(props: PastWeekRowLinkProps): ReactElement {
  return (
    <Link
      to="/appointments/$appointmentId"
      params={{ appointmentId: props.appointmentId }}
      search={props.search}
      className={props.className}
      onClick={props.onClick}
      data-appointment-id={props.appointmentId}
    >
      {props.children}
    </Link>
  );
}

function AgendaFrame(
  props: AppointmentsContentProps & {
    header: AppointmentAgendaHeader;
    liveText: string;
    pastPanel: ReactNode;
    pastBusy: boolean;
  }
): ReactElement {
  const heading = props.scopeSelector ?? (
    <GreetingHeading
      display={props.header.display}
      userName={props.userName}
      headingRef={props.headingRef}
    />
  );

  const weekSlot = useWeekSlot(props);

  return (
    <AppointmentAgenda
      view={props.view}
      onViewChange={props.onViewChange}
      header={props.header}
      agenda={props.agenda.status === "ready" ? props.agenda : null}
      heading={heading}
      viewingOther={props.viewingOther}
      listSearch={props.listSearch}
      showNonDefaultStatuses={props.showNonDefaultStatuses}
      onStatusChange={props.onStatusChange}
      onLoadMore={props.onLoadMore}
      past={props.pastPanel}
      availability={props.availability}
      pastBusy={props.pastBusy}
      liveText={props.liveText}
      week={weekSlot}
    />
  );
}

/**
 * NO-878: the Availability view in the Appointments frame. The title is the
 * viewer's greeting, the RD picker, or (a supervisor on `?rdId=`) the RD's name;
 * the Upcoming and Past answers are never read here.
 */
function AvailabilityFrame(props: AppointmentsContentProps): ReactElement {
  const clock = deviceClock();
  const weekSlot = useWeekSlot(props);

  return (
    <AppointmentAgenda
      view="availability"
      onViewChange={props.onViewChange}
      header={{ display: clock, todayCount: null, healthieCalendarUrl: null }}
      agenda={null}
      heading={
        <AvailabilityTitle
          scopeSelector={props.scopeSelector}
          rdId={props.listSearch.rdId}
          userName={props.userName}
          headingRef={props.headingRef}
          clock={clock}
        />
      }
      viewingOther={props.viewingOther}
      listSearch={props.listSearch}
      showNonDefaultStatuses={false}
      onStatusChange={props.onStatusChange}
      onLoadMore={props.onLoadMore}
      past={null}
      availability={props.availability}
      pastBusy={false}
      liveText=""
      week={weekSlot}
    />
  );
}

function AvailabilityTitle({
  scopeSelector,
  rdId,
  userName,
  headingRef,
  clock,
}: {
  scopeSelector: ReactElement | null;
  rdId: string | undefined;
  userName: string | undefined;
  headingRef: RefObject<HTMLHeadingElement | null>;
  clock: { now: string; timezone: string };
}): ReactElement {
  // Hooks run for every title; the roster is read only for a named RD.
  const named = useViewedRd(scopeSelector === null ? rdId : undefined);

  if (scopeSelector) return scopeSelector;

  if (rdId === undefined) {
    return (
      <GreetingHeading display={clock} userName={userName} headingRef={headingRef} />
    );
  }

  return (
    <h1
      ref={headingRef}
      tabIndex={-1}
      className={cn(
        "truncate font-sans text-3xl font-bold tracking-tight text-foreground outline-none",
        PAGE_TITLE
      )}
    >
      {named?.name ?? "RD hours"}
    </h1>
  );
}

/** The editor, or the choose-an-RD state for an admin with nothing selected. */
function AvailabilityPanel({
  user,
  search,
  onSearchChange,
}: {
  user: AlwaysAiUser;
  search: AppointmentListSearch;
  onSearchChange: (next: AppointmentListSearch) => void;
}): ReactElement {
  const { loads } = availabilityHeading({
    canSelectRd: canSelectAppointmentRd(user),
    rdId: search.rdId,
    holdsOwnHours: can(user, "rd_hours:manage:own"),
  });

  if (!loads) {
    return (
      <div className="mt-6">
        <EmptyState
          icon={CalendarDays}
          title="Choose an RD to see their hours"
          description="Nothing loads until you select an RD."
        />
      </div>
    );
  }

  return (
    <div className="mt-6">
      <RdHoursEditor
        rdId={search.rdId}
        search={search}
        onSearchChange={onSearchChange}
      />
    </div>
  );
}

function deviceClock(): { now: string; timezone: string } {
  return { now: new Date().toISOString(), timezone: deviceTimezone() };
}

function pastClock(past: PastWeekPanelState): { now: string; timezone: string } {
  return "display" in past ? past.display : deviceClock();
}

function pastHeader(
  agenda: AppointmentAgendaState,
  past: PastWeekPanelState
): AppointmentAgendaHeader {
  if (agenda.status === "ready") {
    return {
      display: agenda.display,
      todayCount: agenda.todayCount,
      healthieCalendarUrl: agenda.healthieCalendarUrl,
    };
  }

  return {
    display: pastClock(past),
    todayCount: null,
    healthieCalendarUrl:
      "healthieCalendarUrl" in past ? past.healthieCalendarUrl : null,
  };
}

function isPastBusy(past: PastWeekPanelState): boolean {
  if (past.status === "loading") return true;

  return past.status === "week" && past.phase === "loading";
}

function pastLiveState(past: PastWeekPanelState, silent: boolean): PastLiveState {
  if (silent) return { kind: "silent" };

  if (past.status === "loading") return { kind: "first-loading" };

  if (past.status === "first-error") return { kind: "first-error" };

  if (past.status === "empty") return { kind: "empty" };

  if (past.phase === "loading") return { kind: "loading", target: past.target };

  if (past.phase === "error") return { kind: "error", week: past.week };

  return {
    kind: "ready",
    week: past.week,
    count: past.appointments.length,
    hasEarlier: past.hasEarlier,
  };
}

function appointmentLoading(scopeSelector: ReactElement | null): ReactElement {
  if (!scopeSelector) return <PageMessage>Loading Appointments…</PageMessage>;

  return (
    <ScopedPage heading={scopeSelector}>
      <AgendaSkeleton />
    </ScopedPage>
  );
}

function NoAppointmentSelection({
  heading,
}: {
  heading: ReactElement | null;
}): ReactElement {
  return (
    <ScopedPage heading={heading ?? <MutedHeading>Select an RD</MutedHeading>}>
      <EmptyState
        icon={CalendarDays}
        title="Choose an RD to view their Appointments"
        description="Appointments and Notes are read-only here. Nothing loads until you select an RD."
      />
    </ScopedPage>
  );
}

function UnavailableAppointments({
  reason,
  viewingOther,
  rdName,
  rdMissing,
  heading,
}: {
  reason: Extract<AppointmentAgendaState, { status: "unavailable" }>["reason"];
  viewingOther: boolean;
  rdName: string | null;
  rdMissing: boolean;
  heading: ReactElement | null;
}): ReactElement {
  const copy = appointmentUnavailableCopy({ reason, viewingOther, rdName, rdMissing });

  return (
    <ScopedPage heading={heading}>
      <EmptyState
        icon={CalendarDays}
        title={copy.title}
        description={copy.description}
      />
    </ScopedPage>
  );
}

/** The Appointment Home frame with its title row but no schedule: the states
 *  before, instead of, or while loading one. Same measurements as the agenda so
 *  the title does not move when rows arrive. */
function ScopedPage({
  heading,
  children,
}: {
  heading: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="mx-auto max-w-[1200px] px-6 pb-12 pt-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">{heading}</div>
      </div>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function MutedHeading({ children }: { children: ReactNode }): ReactElement {
  return (
    <h1
      className={cn(
        "font-sans text-3xl font-bold tracking-tight text-subtle",
        PAGE_TITLE
      )}
    >
      {children}
    </h1>
  );
}

const SKELETON_ROW_COUNT = 4;

// Mirrors the schedule card frame so the skeleton and the rows occupy the same
// space; no patient-shaped detail, just the row rhythm.
function AgendaSkeleton(): ReactElement {
  return (
    <div
      role="status"
      aria-label="Loading Appointments"
      className={AGENDA_SKELETON_CARD}
    >
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-4 border-b border-border px-5 py-4 last:border-b-0"
        >
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-40" />
        </div>
      ))}
    </div>
  );
}

function useHeadingFocusAfterFinalBatch(hasMore: boolean) {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const hadMoreRef = useRef(false);
  useEffect(() => {
    if (hadMoreRef.current && !hasMore && document.activeElement === document.body) {
      headingRef.current?.focus({ preventScroll: true });
    }

    hadMoreRef.current = hasMore;
  }, [hasMore]);

  return headingRef;
}

export function AppointmentDetailPage(): ReactElement {
  const { getToken } = useAuth();
  const { appointmentId } = useParams({ strict: false }) as { appointmentId: string };
  const listSearch = useSearch({ strict: false }) as AppointmentListSearch;
  const rdId = listSearch.rdId;
  const queries = useAppointmentDetailQueries(appointmentId, rdId, getToken);
  const readOnly = queries.me.data
    ? !readsOwnAppointmentRows(queries.me.data.user, rdId)
    : rdId !== undefined;

  return (
    <>
      <AppointmentDetailBreadcrumb listSearch={listSearch} />
      <AppointmentDetailContent
        appointmentId={appointmentId}
        rdId={rdId}
        listSearch={listSearch}
        readOnly={readOnly}
        getToken={getToken}
        queries={queries}
      />
    </>
  );
}

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

function useAppointmentDetailQueries(
  appointmentId: string,
  rdId: string | undefined,
  getToken: ClerkGetToken
) {
  const me = useQuery({
    queryKey: ["current-user"],
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });
  // The selection scopes the read, so it belongs in the key as well as the URL:
  // the same appointment id under a different selection is a different request.
  const scopeSuffix = rdId ? `?rdId=${encodeURIComponent(rdId)}` : "";
  const appointment = useQuery({
    queryKey: ["appointment", appointmentId, rdId ?? null],
    queryFn: () =>
      authenticatedApi<{ data: AppointmentDetailResponse }>(
        getToken,
        `/api/appointments/${encodeURIComponent(appointmentId)}${scopeSuffix}`
      ),
    staleTime: 0,
    gcTime: 0,
  });
  const note = useQuery({
    queryKey: ["appointment-note", appointmentId, rdId ?? null],
    queryFn: () =>
      authenticatedApi<{ data: AppointmentNoteResponse }>(
        getToken,
        `/api/appointments/${encodeURIComponent(appointmentId)}/note${scopeSuffix}`
      ),
    staleTime: 0,
    gcTime: 0,
  });

  return { appointment, note, me };
}

type AppointmentDetailQueries = ReturnType<typeof useAppointmentDetailQueries>;

function AppointmentDetailContent({
  appointmentId,
  rdId,
  listSearch,
  readOnly,
  getToken,
  queries,
}: {
  appointmentId: string;
  rdId: string | undefined;
  listSearch: AppointmentListSearch;
  readOnly: boolean;
  getToken: ClerkGetToken;
  queries: AppointmentDetailQueries;
}): ReactElement {
  if (!queries.appointment.data || !queries.note.data || !queries.me.data) {
    return <AppointmentDetailPending queries={queries} />;
  }

  return (
    <AppointmentDetail
      key={`${appointmentId}:${rdId ?? "own"}`}
      item={queries.appointment.data.data}
      listSearch={listSearch}
      readOnly={readOnly}
      timezone={deviceTimezone()}
      note={queries.note.data.data}
      getToken={getToken}
    />
  );
}

function AppointmentDetailPending({
  queries,
}: {
  queries: AppointmentDetailQueries;
}): ReactElement {
  const { appointment, note, me } = queries;

  if (!appointment.isError && !note.isError && !me.isError) {
    return <PageMessage>Loading Appointment…</PageMessage>;
  }

  const error = appointment.isError
    ? appointment.error
    : detailSecondaryError(note.error, me.error);

  return (
    <QueryErrorState
      error={error}
      notFoundText="Appointment not found."
      onRetry={() => {
        void appointment.refetch();
        void note.refetch();
        void me.refetch();
      }}
      className="h-full"
    />
  );
}

function detailSecondaryError(
  noteError: Error | null,
  meError: Error | null
): Error | null {
  if (noteError) return noteError;

  return meError;
}

function AppointmentDetailBreadcrumb({
  listSearch,
}: {
  listSearch: AppointmentListSearch;
}): ReactElement {
  // The tail stays the generic "Appointment": the patient's name is PHI and the
  // bar is chrome that persists across navigation, so it must not carry one.
  // The Appointments href keeps the selection so going back does not silently
  // drop the admin's RD, and keeps the view, Past week, and toggle (NO-767).
  return (
    <BreadcrumbSetter
      segments={[
        { label: "Appointments", href: appointmentListHref(listSearch) },
        { label: "Appointment" },
      ]}
    />
  );
}

function PageMessage({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="flex h-full items-center justify-center px-6 text-center text-muted-foreground">
      <CalendarDays aria-hidden="true" className="mr-2 size-5" />
      {children}
    </div>
  );
}
